import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileSignature, Loader2, Upload, CheckCircle2, ShieldCheck } from "lucide-react";

const PLATFORMS = [
  { value: "snapdocs", label: "Snapdocs" },
  { value: "notary rotary", label: "Notary Rotary" },
  { value: "123notary", label: "123Notary" },
  { value: "other", label: "Other" },
];

const STATUS_META = {
  admin_verified: { label: "Verified by ARPS", tone: "border-emerald-200 bg-emerald-50 text-emerald-700", Icon: ShieldCheck },
  self_reported: { label: "Self-reported", tone: "border-sky-200 bg-sky-50 text-sky-700", Icon: CheckCircle2 },
  not_provided: { label: "Not provided", tone: "border-slate-200 bg-slate-50 text-slate-500", Icon: FileSignature },
};

export const SigningAgentCard = () => {
  const [form, setForm] = useState({ nna_number: "", platform: "", profile_ref: "", experience_years: "", signings_completed: "", file_id: null });
  const [fileName, setFileName] = useState("");
  const [status, setStatus] = useState("not_provided");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/provider/signing-agent").then(({ data }) => {
      setForm({
        nna_number: data.nna_number || "", platform: data.platform || "", profile_ref: data.profile_ref || "",
        experience_years: data.experience_years ?? "", signings_completed: data.signings_completed ?? "",
        file_id: data.file_id || null,
      });
      setStatus(data.verification_status || "not_provided");
    }).catch(() => {});
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, file_id: data.id }));
      setFileName(file.name);
      toast.success("File attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        nna_number: form.nna_number.trim(), platform: form.platform, profile_ref: form.profile_ref.trim(),
        experience_years: form.experience_years === "" ? null : Number(form.experience_years),
        signings_completed: form.signings_completed === "" ? null : Number(form.signings_completed),
        file_id: form.file_id,
      };
      const { data } = await api.put("/provider/signing-agent", payload);
      setStatus(data.verification_status || "not_provided");
      toast.success("Signing-agent details saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const sm = STATUS_META[status] || STATUS_META.not_provided;
  const SIcon = sm.Icon;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="signing-agent-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
            <FileSignature className="h-5 w-5 text-indigo-500" /> Loan signing / signing agent
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Optional. If you're a certified loan-signing agent, add your details to be prioritized for
            loan-signing notary work. Every field is optional and visible only to you and ARPS admins.
          </p>
        </div>
        <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${sm.tone}`} data-testid="signing-agent-status">
          <SIcon className="h-3.5 w-3.5" /> {sm.label}
        </span>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs">NNA number</Label>
          <Input data-testid="signing-agent-nna" value={form.nna_number} onChange={(e) => setForm({ ...form, nna_number: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Platform</Label>
          <select data-testid="signing-agent-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
            <option value="">Select…</option>
            {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Profile ID or URL</Label>
          <Input data-testid="signing-agent-profile" value={form.profile_ref} onChange={(e) => setForm({ ...form, profile_ref: e.target.value })} placeholder="e.g. your Snapdocs profile link" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Years of experience</Label>
          <Input data-testid="signing-agent-years" type="number" min="0" step="0.5" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Approx. signings completed</Label>
          <Input data-testid="signing-agent-signings" type="number" min="0" value={form.signings_completed} onChange={(e) => setForm({ ...form, signings_completed: e.target.value })} className="mt-1" />
        </div>
      </div>

      <div className="mt-3">
        <Label className="text-xs">Supporting file (optional)</Label>
        <div className="mt-1 flex items-center gap-3">
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif" onChange={upload} className="hidden" data-testid="signing-agent-file-input" />
          <Button variant="outline" size="sm" data-testid="signing-agent-file-btn" disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="mr-2 h-4 w-4" /> Attach file</>}
          </Button>
          {form.file_id && <span className="inline-flex items-center gap-1 text-xs text-emerald-700" data-testid="signing-agent-file-attached"><CheckCircle2 className="h-3.5 w-3.5" /> {fileName || "File attached"}</span>}
        </div>
      </div>

      <Button data-testid="signing-agent-save" onClick={save} disabled={saving} className="mt-4 bg-slate-900 text-white hover:bg-slate-800">
        {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</> : "Save details"}
      </Button>
    </div>
  );
};
